import { types as sdkTypes } from './sdkLoader';
import { isTransactionsTransitionInvalidTransition } from './errors';
import * as log from './log';
import { PRACTITIONER_ONBOARDING_PROCESS_NAME } from '../transactions/transaction';
import { transitions } from '../transactions/transactionProcessPractitionerOnboarding';
import { CHECKLIST_STEP_ID, getPractitionerChecklistState } from './practitionerChecklist';

const { UUID } = sdkTypes;

export const PRACTITIONER_ONBOARDING_TX_PRIVATE_DATA_KEY = 'practitionerOnboardingTxId';

/** One-time backfill wave so incomplete practitioners get the retitled TX welcome + P3D. */
export const PRACTITIONER_ONBOARDING_EMAIL_WAVE_KEY = 'practitionerOnboardingEmailWave';
export const PRACTITIONER_ONBOARDING_EMAIL_WAVE = 'up-and-running-v1';

export const PRACTITIONER_ONBOARDING_PROCESS_ALIAS = `${PRACTITIONER_ONBOARDING_PROCESS_NAME}/release-1`;

const CHECKLIST_PROTECTED_DATA_KEYS = [
  'verifyEmailDone',
  'completeProfileDone',
  'createListingDone',
  'addPayoutDone',
  'shareListingDone',
  'checklistPercent',
];

// Only active onboarding states — completed/expired TXs must not block a fresh initiate.
const ACTIVE_ONBOARDING_LAST_TRANSITIONS = [
  transitions.START_ONBOARDING,
  transitions.UPDATE_CHECKLIST,
];

// Deduplicate concurrent start calls (verify + dashboard loadData can race before
// privateData.practitionerOnboardingTxId is written). Keyed by user id.
const inFlightStartsByUserId = new Map();

export const getPractitionerOnboardingListingId = () => {
  // CRA/webpack DefinePlugin inlines `process.env.REACT_APP_*` as string literals.
  // Do NOT gate on `typeof process === 'undefined'` — in the browser `process` is
  // absent, so that check always returned null and skipped welcome-email initiate
  // even when the listing id was correctly baked into the bundle.
  return process.env.REACT_APP_PRACTITIONER_ONBOARDING_LISTING_ID || null;
};

/**
 * Map checklist state → TX protectedData keys used by email templates.
 * @param {{ steps: Array<{id: string, done: boolean}>, percent: number }} checklist
 * @returns {Object}
 */
export const checklistStateToProtectedData = checklist => {
  const byId = {};
  (checklist?.steps || []).forEach(step => {
    byId[step.id] = !!step.done;
  });
  return {
    verifyEmailDone: !!byId[CHECKLIST_STEP_ID.VERIFY_EMAIL],
    completeProfileDone: !!byId[CHECKLIST_STEP_ID.COMPLETE_PROFILE],
    createListingDone: !!byId[CHECKLIST_STEP_ID.CREATE_LISTING],
    addPayoutDone: !!byId[CHECKLIST_STEP_ID.ADD_PAYOUT],
    shareListingDone: !!byId[CHECKLIST_STEP_ID.SHARE_LISTING],
    checklistPercent: typeof checklist?.percent === 'number' ? checklist.percent : 0,
  };
};

const protectedDataMatchesChecklist = (protectedData, checklistData) => {
  if (!protectedData || !checklistData) {
    return false;
  }
  return CHECKLIST_PROTECTED_DATA_KEYS.every(key => protectedData[key] === checklistData[key]);
};

const getOnboardingTxId = currentUser =>
  currentUser?.attributes?.profile?.privateData?.[PRACTITIONER_ONBOARDING_TX_PRIVATE_DATA_KEY] ||
  null;

export const hasCurrentOnboardingEmailWave = currentUser =>
  currentUser?.attributes?.profile?.privateData?.[PRACTITIONER_ONBOARDING_EMAIL_WAVE_KEY] ===
  PRACTITIONER_ONBOARDING_EMAIL_WAVE;

/**
 * Active onboarding TXs (welcome sent / P3D pending). Completed/expired must not
 * block a fresh initiate for still-incomplete practitioners.
 *
 * @param {Object|null} tx
 * @returns {boolean}
 */
export const isActiveOnboardingTx = tx => {
  if (!tx) {
    return false;
  }
  return ACTIVE_ONBOARDING_LAST_TRANSITIONS.includes(tx?.attributes?.lastTransition);
};

/**
 * Detect an onboarding TX that was started while the user's email was still
 * unverified. Sharetribe drops TX emails to unverified addresses and never
 * resends them. After the emailVerified gate, new initiates always write
 * verifyEmailDone=true, so an active TX with verifyEmailDone !== true is a
 * pre-verify leftover that must be retired and re-initiated.
 *
 * @param {Object|null} tx
 * @returns {boolean}
 */
export const isPreVerifyOnboardingTx = tx => {
  if (!isActiveOnboardingTx(tx)) {
    return false;
  }
  return !tx?.attributes?.protectedData?.verifyEmailDone;
};

/**
 * Resolve checklist used when writing protectedData on start-onboarding.
 * Prefer an explicit checklist (dashboard backfill with listings + Stripe);
 * otherwise compute from currentUser (+ optional ownListings / stripeAccount).
 *
 * @param {Object} currentUser
 * @param {{ checklist?: Object, ownListings?: Array, stripeAccount?: Object }} [options]
 * @returns {{ steps: Array, percent: number, isComplete: boolean }}
 */
export const resolveOnboardingChecklist = (currentUser, options = {}) => {
  if (options.checklist) {
    return options.checklist;
  }
  return getPractitionerChecklistState({
    currentUser,
    ownListings: options.ownListings || [],
    stripeAccount: options.stripeAccount || null,
  });
};

/**
 * Start the practitioner onboarding transaction (sends welcome email + schedules P3D reminder).
 * Idempotent via privateData.practitionerOnboardingTxId + practitionerOnboardingEmailWave
 * for healthy (post-verify) TXs on the current email wave.
 * If a prior TX was started while unverified (welcome dropped), completes it, clears the
 * idempotency key, and initiates a fresh TX so welcome is delivered once.
 *
 * Existing verified practitioners with an incomplete Getting Started checklist are
 * backfilled on `/dashboard` (including expired/completed TX pointers, and one-time
 * re-initiate when the email wave key is missing — e.g. process v7 had no welcome).
 * Fully complete checklists skip initiate so we do not spam finished practitioners.
 *
 * @param {Object} sdk Sharetribe SDK
 * @param {Object} currentUser
 * @param {{ checklist?: Object, ownListings?: Array, stripeAccount?: Object }} [options]
 * @returns {Promise<Object|null>} transaction or null if skipped
 */
export const startPractitionerOnboardingTransaction = (sdk, currentUser, options = {}) => {
  const listingIdRaw = getPractitionerOnboardingListingId();
  if (!listingIdRaw) {
    log.error(
      new Error('Missing REACT_APP_PRACTITIONER_ONBOARDING_LISTING_ID'),
      'practitioner-onboarding-missing-listing-id',
      {}
    );
    return Promise.resolve(null);
  }

  if (!currentUser?.id) {
    log.error(
      new Error('startPractitionerOnboardingTransaction called without currentUser'),
      'practitioner-onboarding-skipped-no-user',
      {}
    );
    return Promise.resolve(null);
  }

  // Sharetribe does not deliver transaction-process emails to unverified addresses,
  // and skipped notifications are never resent after verify. Initiate only once
  // emailVerified is true (email-verify hook + dashboard loadData are the backups).
  if (!currentUser?.attributes?.emailVerified) {
    return Promise.resolve(null);
  }

  const userId = currentUser.id.uuid || currentUser.id;
  const existingInFlight = inFlightStartsByUserId.get(userId);
  if (existingInFlight) {
    return existingInFlight;
  }

  const storeOnboardingTxId = (txId, attempt = 1) =>
    sdk.currentUser
      .updateProfile({
        privateData: {
          [PRACTITIONER_ONBOARDING_TX_PRIVATE_DATA_KEY]: txId,
          [PRACTITIONER_ONBOARDING_EMAIL_WAVE_KEY]: PRACTITIONER_ONBOARDING_EMAIL_WAVE,
        },
      })
      .catch(e => {
        log.error(e, 'practitioner-onboarding-store-tx-id-failed', { txId, attempt });
        if (attempt < 2) {
          return storeOnboardingTxId(txId, attempt + 1);
        }
        // Do not treat as success — leaving privateData unset risks duplicate initiates.
        throw e;
      });

  const clearOnboardingTxId = () =>
    sdk.currentUser.updateProfile({
      privateData: {
        [PRACTITIONER_ONBOARDING_TX_PRIVATE_DATA_KEY]: null,
      },
    });

  const retireStaleOnboardingTx = txId =>
    sdk.transactions
      .transition({
        id: new UUID(txId),
        transition: transitions.COMPLETE_ONBOARDING,
        params: {},
      })
      .catch(e => {
        // Already completed / expired is fine — still clear the idempotency key.
        if (isTransactionsTransitionInvalidTransition(e)) {
          return null;
        }
        throw e;
      })
      .then(() =>
        clearOnboardingTxId().catch(e => {
          log.error(e, 'practitioner-onboarding-clear-tx-id-failed', { txId });
          throw e;
        })
      );

  const queryActiveOnboardingTx = () =>
    sdk.transactions
      .query({
        only: 'order',
        lastTransitions: ACTIVE_ONBOARDING_LAST_TRANSITIONS,
        page: 1,
        perPage: 1,
      })
      .then(response => response?.data?.data?.[0] || null)
      .catch(e => {
        log.error(e, 'practitioner-onboarding-lookup-failed', {});
        return null;
      });

  const showOnboardingTx = txId =>
    sdk.transactions
      .show({ id: new UUID(txId) })
      .then(response => response?.data?.data || null)
      .catch(e => {
        log.error(e, 'practitioner-onboarding-show-failed', { txId });
        return null;
      });

  const initiateFresh = () => {
    // Welcome fires only after verify — verifyEmailDone will be true in protectedData.
    // Pass current checklist flags at start-onboarding so templates show leftover vs done.
    const checklist = resolveOnboardingChecklist(currentUser, options);
    if (checklist.isComplete) {
      // No welcome / P3D for practitioners who already finished Getting Started.
      return Promise.resolve(null);
    }
    const checklistProtectedData = checklistStateToProtectedData(checklist);

    return sdk.transactions
      .initiate(
        {
          processAlias: PRACTITIONER_ONBOARDING_PROCESS_ALIAS,
          transition: transitions.START_ONBOARDING,
          params: {
            listingId: new UUID(listingIdRaw),
            protectedData: {
              purpose: 'practitioner-onboarding',
              ...checklistProtectedData,
            },
          },
        },
        { expand: true }
      )
      .then(response => {
        const tx = response?.data?.data;
        const txId = tx?.id?.uuid;
        if (!txId) {
          log.error(
            new Error('Practitioner onboarding initiate returned no transaction id'),
            'practitioner-onboarding-initiate-empty',
            {}
          );
          return null;
        }
        return storeOnboardingTxId(txId)
          .then(() => tx)
          .catch(() => null);
      })
      .catch(e => {
        log.error(e, 'practitioner-onboarding-initiate-failed', {});
        return null;
      });
  };

  const existingTxId = getOnboardingTxId(currentUser);
  const waveDone = hasCurrentOnboardingEmailWave(currentUser);

  const loadCandidateTx = () => {
    if (existingTxId) {
      return showOnboardingTx(existingTxId).then(tx => {
        if (tx) {
          return tx;
        }
        // Stale privateData pointer — fall back to querying active onboarding TXs.
        return queryActiveOnboardingTx();
      });
    }
    return queryActiveOnboardingTx();
  };

  const retireThenInitiate = staleTxId =>
    retireStaleOnboardingTx(staleTxId)
      .then(() => initiateFresh())
      .catch(e => {
        log.error(e, 'practitioner-onboarding-reinitiate-failed', { staleTxId });
        return null;
      });

  const run = loadCandidateTx().then(existing => {
    if (existing && isPreVerifyOnboardingTx(existing)) {
      // Retire dropped pre-verify TX; only re-initiate when checklist still incomplete.
      return retireThenInitiate(existing.id?.uuid);
    }

    if (existing && isActiveOnboardingTx(existing)) {
      if (waveDone) {
        // Healthy post-verify TX on current email wave — no second welcome.
        const txId = existing.id?.uuid;
        if (!existingTxId && txId) {
          return storeOnboardingTxId(txId)
            .then(() => existing)
            .catch(() => existing);
        }
        return null;
      }
      // Incomplete practitioners missing the current wave (no prior TX welcome, or
      // process v7 reminder-only): one-time retire + send retitled welcome + P3D.
      return retireThenInitiate(existing.id?.uuid);
    }

    if (existing) {
      // Completed / expired TX still referenced — clear pointer and backfill if incomplete.
      return clearOnboardingTxId()
        .then(() => initiateFresh())
        .catch(e => {
          log.error(e, 'practitioner-onboarding-expired-reinitiate-failed', {
            txId: existing.id?.uuid,
          });
          return null;
        });
    }

    return initiateFresh();
  });

  inFlightStartsByUserId.set(userId, run);
  return run.finally(() => {
    if (inFlightStartsByUserId.get(userId) === run) {
      inFlightStartsByUserId.delete(userId);
    }
  });
};

/**
 * Sync Getting Started checklist flags onto the onboarding TX protectedData
 * (for P3D reminder email conditionals). No-op if unchanged or TX missing/finished.
 *
 * @param {Object} sdk
 * @param {Object} currentUser
 * @param {{ steps: Array, percent: number, isComplete: boolean }} checklist
 * @returns {Promise<Object|null>}
 */
export const syncPractitionerOnboardingChecklist = (sdk, currentUser, checklist) => {
  const txId = getOnboardingTxId(currentUser);
  if (!txId || !checklist || checklist.isComplete) {
    return Promise.resolve(null);
  }

  const checklistData = checklistStateToProtectedData(checklist);

  return sdk.transactions
    .show({ id: new UUID(txId) })
    .then(response => {
      const tx = response?.data?.data;
      const lastTransition = tx?.attributes?.lastTransition;
      if (
        lastTransition === transitions.COMPLETE_ONBOARDING ||
        lastTransition === transitions.EXPIRE_ONBOARDING
      ) {
        return null;
      }

      const existing = tx?.attributes?.protectedData || {};
      if (protectedDataMatchesChecklist(existing, checklistData)) {
        return null;
      }

      return sdk.transactions
        .transition({
          id: new UUID(txId),
          transition: transitions.UPDATE_CHECKLIST,
          params: {
            protectedData: {
              purpose: 'practitioner-onboarding',
              ...checklistData,
            },
          },
        })
        .then(res => res?.data?.data || null);
    })
    .catch(e => {
      if (isTransactionsTransitionInvalidTransition(e)) {
        return null;
      }
      log.error(e, 'practitioner-onboarding-sync-checklist-failed', { txId });
      return null;
    });
};

/**
 * Complete onboarding TX to cancel the delayed P3D reminder email.
 *
 * @param {Object} sdk
 * @param {Object} currentUser
 * @param {{ steps: Array, percent: number }|null} [checklist]
 * @returns {Promise<Object|null>}
 */
export const completePractitionerOnboardingTransaction = (sdk, currentUser, checklist = null) => {
  const txId = getOnboardingTxId(currentUser);
  if (!txId) {
    return Promise.resolve(null);
  }

  const params = checklist
    ? {
        protectedData: {
          purpose: 'practitioner-onboarding',
          ...checklistStateToProtectedData(checklist),
        },
      }
    : {};

  return sdk.transactions
    .transition({
      id: new UUID(txId),
      transition: transitions.COMPLETE_ONBOARDING,
      params,
    })
    .then(response => response?.data?.data || null)
    .catch(e => {
      // Already completed / expired is fine — treat as success so callers do not retry forever.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return null;
      }
      log.error(e, 'practitioner-onboarding-complete-failed', { txId });
      throw e;
    });
};

export const isPractitionerOnboardingProcess = processName => {
  const base = typeof processName === 'string' ? processName.split('/')[0].trim() : '';
  return base === PRACTITIONER_ONBOARDING_PROCESS_NAME;
};
